/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  AelNavigationParams,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  ConstructionScheduleRestService,
  ContactRestService,
  MaintenanceScheduleRestService,
  PreConstructionScheduleRestService,
  SalesScheduleRestService,
  ScheduleDetailsDto,
  ScheduleTaskType,
  ScheduleType,
  TaskRelationType,
} from '@gh/builder-shared-data';
import { TimerEvent } from '@gh/common-internal-shared';
import {
  ActionFlow,
  AppRouter,
  DEFAULT_AUTOSAVE_INTERVALS,
  EnumDictionary,
  getRouteInitData,
  getRouteParentTitle,
  getRouteStaticData,
  isDirtyTypeNew,
  MutablePageState,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { Permission } from '@gh/core-auth';
import { TranslationService, TranslationTupleOrId } from '@gh/core-mls';
import {
  Confirmation,
  DialogService,
  FormMode,
  noAnswer,
  NotificationService,
  WaitingContext,
  WaitingMode,
  yesAnswer,
  yesNoCancelConfirmation,
  yesNoConfirmation,
} from '@gh/core-ui';
import { GridLayout } from '@gh/core-ui-grid';
import { currentNormalizedDate, getLocalValue, openInNewWindow, setLocalValue } from '@gh/core-util';
import { filter$, first$, map$, switchMap$ } from '@gh/rx/operators';
import {
  CalendarListItem,
  GanttChartComponent,
  GanttTask,
  getGanttTaskId,
  isGanttTaskCanBeOpened,
  isGanttTaskCanHaveSubtasks,
} from '@gh/builder-shared-gantt-chart';
import { ScheduleGanttDetailsBusinessService } from '../../resources/schedule-gantt-details-business.service';
import {
  newScheduleCloneTask,
  newScheduleEmptyTask,
  ScheduleGanttDetails,
  scheduleGanttDetailsFromDto,
  scheduleGanttDetailsToDto,
  ScheduleGanttTask,
} from '../../resources/schedule-gantt-details.model';
import { max, omit, values } from 'lodash';
import { MessageSeverity } from '@gh/core-messages';
import { formatDate } from '@angular/common';
import { Observable } from 'rxjs';
import { of$ } from '@gh/rx';
import { WaitingProcess } from '../../../core-ui/waiting/waiting-process';
import { scheduleCheckUsageDialogRoute } from '../schedule-check-usage-dialog/schedule-check-usage-dialog.route';

const SCHEDULE_GANTT_AUTOSAVE_LOCAL_KEY = 'schedule-gantt:autosave';

@Component({
  selector: 'gh-schedule-gantt-details',
  template: require('./schedule-gantt-details.component.html'),
  styles: [require('./schedule-gantt-details.component.scss')],
})
export class ScheduleGanttDetailsComponent extends AbstractFormComponent<ScheduleGanttDetails> implements OnInit {
  @ViewChild(GanttChartComponent, { static: false }) gantt: GanttChartComponent;
  @Input() currentDate = currentNormalizedDate();

  autosaveIntervals = DEFAULT_AUTOSAVE_INTERVALS;
  currentAutosaveInterval = getLocalValue(SCHEDULE_GANTT_AUTOSAVE_LOCAL_KEY, '30m');

  gridLayout: GridLayout;
  taskTypeDict: EnumDictionary<ScheduleTaskType>;
  relationshipTypeDict: EnumDictionary<TaskRelationType>;
  showBaseDates: boolean;
  details: ScheduleGanttDetails;
  tasks$: Observable<GanttTask[]> = new Observable<GanttTask[]>();
  form: MutablePageState<ScheduleGanttDetails>;
  calendar: CalendarListItem[];
  reportUrl: string;
  initialRatio: number;
  defaultInitialRatio: number = 0.5;
  nextTaskNumber: number;
  waitingProcess: WaitingProcess;
  saveBaseDatesAnswer?: Confirmation;
  scheduleType: ScheduleType = getRouteStaticData(this.route)['type'];
  scheduleName: string;
  isUndoDisabled$: Observable<boolean>;
  isRedoDisabled$: Observable<boolean>;

  constructor(private route: ActivatedRoute,
              private ngZone: NgZone,
              private router: AppRouter,
              private translations: TranslationService,
              private userContext: UserContext,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private navigations: BuilderNavigationService,
              private contactRest: ContactRestService,
              private commonActions: CommonActions,
              private pipelines: FormActionPipelines,
              private waitingContext: WaitingContext,
              private constructionRest: ConstructionScheduleRestService,
              private preConstructionRest: PreConstructionScheduleRestService,
              private salesRest: SalesScheduleRestService,
              private notification: NotificationService,
              private maintenanceRest: MaintenanceScheduleRestService,
              private business: ScheduleGanttDetailsBusinessService) {
    super();
    this.saveBeforeSaveNote = this.saveBeforeSaveNote.bind(this);
  }

  get parentTitle(): TranslationTupleOrId {
    return getRouteParentTitle(this.route) || this.parentTitleStatic;
  }

  get helpId(): string {
    switch (this.scheduleType) {
      case ScheduleType.Construction:
        return 'schedules/constructions/gantt-chart';
      case ScheduleType.PreConstruction:
        return 'schedules/pre-constructions/gantt-chart';
      case ScheduleType.Maintenance:
        return 'schedules/warranty/gantt-chart';
      case ScheduleType.Sales:
        return 'schedules/sales/gantt-chart';
      default:
        return '';
    }
  }
  get parentTitleStatic(): string {
    switch (this.scheduleType) {
      case ScheduleType.Construction:
        return 'lbl_construction_schedules';
      case ScheduleType.PreConstruction:
        return 'lbl_pre_construction_schedules';
      case ScheduleType.Maintenance:
        return 'lbl_maintenance_schedules';
      case ScheduleType.Sales:
        return 'lbl_sales_schedules';
      default:
        return '';
    }
  }
  get titlePrefix(): string {
    switch (this.scheduleType) {
      case ScheduleType.Construction:
        return 'lbl_construction_sch_for_job';
      case ScheduleType.PreConstruction:
        return 'lbl_pre_construction_sch_for_job';
      case ScheduleType.Maintenance:
        return 'lbl_maintenance_sch_for_job';
      case ScheduleType.Sales:
        return 'lbl_sales_sch_for_sf';
      default:
        return '';
    }
  }

  get name(): string {
    if (this.scheduleType === ScheduleType.Sales) {
      return this.form.value.salesFolderName;
    } else {
      return String(this.form.value.jobNumber);
    }
  }

  get tasks(): GanttTask[] {
    return this.form.value.tasks;
  }

  get completeFlow(): ActionFlow<FormFlowState<ScheduleGanttDetails>> {
    const bounds = this.gantt.api.calculateBounds();
    this.form.value.startDate = bounds.startDate;
    this.form.value.endDate = bounds.endDate;
    this.form.value.duration = bounds.duration;
    this.form.value.tasks = <ScheduleGanttTask[]>this.gantt.api.getAllTasks();
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        () => this.gantt.api.isInvalid(),
        () => this.handleErrors()),
      execute: this.commonActions.execute(() => this.schSaveBaseDatesDialog()
                                                    .pipe(switchMap$(
                                                      () => this.saveData().pipe(map$(
                                                        (response) => {
                                                          this.saveBaseDatesAnswer = undefined;
                                                          this.waitingProcess.stop();
                                                          if (this.isNewSchedule()) {
                                                            this.details.scheduleId = response.scheduleId;
                                                            return {
                                                              isNew: true,
                                                              ...response,
                                                            };
                                                          }
                                                          this.details.scheduleId = response.scheduleId;
                                                          return response;
                                                          // tslint:disable-next-line:align
                                                        })))), true, (error: any) => {
        this.notification.processAll(error);
        this.waitingProcess.stop();
      }),
      finish: ({ value }) => {
        if ((value['isNew'] ||  this.isNewSchedule()) && value.scheduleId) {
          this.reloadData(omit(value, 'isNew'), true);
          this.navigateToScheduleTemplateEdit(value.scheduleId);
          return;
        }
        this.reloadData(value, true);
      },
      error: (error) => {
        this.waitingProcess.stop();
      },
    });
  }

  get isConstruction(): boolean {
    return this.scheduleType === ScheduleType.Construction;
  }

  get hasReportPermission(): boolean {
    return this.userContext.hasPermission(BuilderPermission.Reports.Schedules.CONSTRUCTION_SCHEDULE);
  }

  ngOnInit(): void {
    const { calendar, details, gridLayout, reportUrl, taskTypeDict, relationshipTypeDict, filter } = getRouteInitData(this.route);
    this.initialRatio = filter?.primary || this.defaultInitialRatio;
    this.details = details;
    this.scheduleName = details.type === ScheduleType.Sales ? details.salesFolderName : details.jobNumber;
    this.reportUrl = reportUrl;
    this.taskTypeDict = taskTypeDict;
    this.relationshipTypeDict = relationshipTypeDict;
    this.gridLayout = gridLayout;
    this.calendar = calendar ? calendar.dates : [];
    const formMode = this.isNewSchedule() ? FormMode.CreateMode :
      this.userContext.hasPermission(this.getEditPermission()) && this.isScheduleEditable(this.details)
        ? FormMode.EditMode
        : FormMode.ViewMode;
    this.form = new MutablePageState(formMode, this.details);
    this.nextTaskNumber = (max(values(this.details.tasks).map((task) => task.taskNumber)) || 0) + 1;
    this.showNotificationEndDate();
    this.setHistoryObservable();
    this.subscribeOnChanges();
  }

  setHistoryObservable(): void {
    if (!this.gantt || !this.gantt.api) {
      // tslint:disable-next-line:no-magic-numbers
      setTimeout(() => this.setHistoryObservable(), 100);
      return;
    }
    this.gantt.api.initHistory();
    this.isUndoDisabled$ = this.gantt.api.isUndoDisabled$;
    this.isRedoDisabled$ = this.gantt.api.isRedoDisabled$;
  }

  subscribeOnChanges(): void {
    if (!this.gantt || !this.gantt.api) {
      // tslint:disable-next-line:no-magic-numbers
      setTimeout(() => this.subscribeOnChanges(), 100);
      return;
    }
    this.tasks$ = this.gantt.api.getAllTasks$();
  }

  saveData(): Observable<ScheduleGanttDetails> {
    this.waitingProcess = this.waitingContext.start(WaitingMode.Blocked);
    return this.isNewSchedule() ? this.business.create(this.scheduleType, this.form.submitValue, this.saveBaseDatesAnswer)
    : this.business.update(this.scheduleType, this.form.submitValue, this.saveBaseDatesAnswer);
  }

  reloadData(details: ScheduleGanttDetails, clearDirty?: boolean): void {
    this.details = details;
    this.form.mode = this.isNewSchedule() ? FormMode.CreateMode :
      this.userContext.hasPermission(this.getEditPermission()) && this.isScheduleEditable(details)
        ? FormMode.EditMode
        : FormMode.ViewMode;
    this.form.setValue({
                         ...details,
      tasks: details.tasks.map((task) => {
        const target = this.form.value.tasks.find((formTask) => formTask.taskNumber === task.taskNumber);
        return {
          ...task,
          opened: target ? target.opened : false,
          parentTaskId: target && target.parentTaskId ? target.parentTaskId : task.parentTaskId,
          taskId: target && target.taskId ? target.taskId : task.taskId,
          dirtyType: target && !clearDirty ? target.dirtyType : undefined,
        };
      }),
    });
    if (!clearDirty) {
      this.gantt.api.addToHistory();
    } else {
      this.form.resetDirty();
      this.gantt.api.resetDirty();
      setTimeout(() => this.gantt.api.initHistory());
    }
    // tslint:disable-next-line:no-magic-numbers
    setTimeout(() => {
      this.gantt.api.reDrawRows();
      this.gantt.refreshTimeLine();
      // tslint:disable-next-line:no-magic-numbers
    },         50);
  }

  showNotificationEndDate(): void {
    if (this.scheduleType !== ScheduleType.Construction) {
      return;
    }
    // tslint:disable-next-line:no-magic-numbers
    const msInDay = 1000 * 60 * 60 * 24;
    const currentDate = new Date();
    const endDate: Date = this.details.endDate || new Date();
    if (endDate.getTime() < currentDate.getTime() &&
      formatDate(endDate, 'shortDate', 'en-US') !==  formatDate(currentDate, 'shortDate', 'en-US')) {
      return;
    }
    // tslint:disable-next-line:no-magic-numbers
    if (endDate.getTime() < currentDate.getTime() + msInDay * 7) {
      this.notification.show({
        severity: MessageSeverity.Success,
        messageId: 'msg_end_date_7_days',
      });
      // tslint:disable-next-line:no-magic-numbers
    } else if (endDate.getTime() < currentDate.getTime() + msInDay * 14) {
      this.notification.show({
         severity: MessageSeverity.Success,
         messageId: 'msg_end_date_14_days',
      });
      // tslint:disable-next-line:no-magic-numbers
    } else if (endDate.getTime() < currentDate.getTime() + msInDay * 30) {
      this.notification.show({
        severity: MessageSeverity.Success,
        messageId: 'msg_end_date_30_days',
      });
    }
  }

  isNewSchedule(): boolean {
    return !this.details.scheduleId || this.details.scheduleId === 'new';
  }

  onShowCompletedTasksChange(show: boolean): void {
    this.gantt.api.showCompletedTasks(show);
  }

  onShowDeletedTasksChange(show: boolean): void {
    this.gantt.api.showDeletedTasks(show);
  }

  onTaskAdd(): void {
    const selectedTask = this.gantt.api.getSelectedTask();
    if (!selectedTask) {
      this.addEmptyTask();
      return;
    }
    const taskIndex = this.gantt.api.getTaskIndex(selectedTask.taskId);
    this.addEmptyTask(selectedTask.parentTaskId, taskIndex + 1);
  }

  onSiblingTaskAdd(sourceSibling: GanttTask): void {
    const taskIndex = this.gantt.api.getTaskIndex(getGanttTaskId(sourceSibling));
    this.addEmptyTask(sourceSibling.parentTaskId, taskIndex + 1);
  }

  onSubTaskAdd(parentTask: GanttTask): void {
    this.addEmptyTask(getGanttTaskId(parentTask));
  }

  onNotSubTask(task: GanttTask): void {
    this.gantt.api.bubbleTask(getGanttTaskId(task));
  }

  onTaskOpen(task: GanttTask): void {
    if (this.form.dirty || this.form.mode === FormMode.CreateMode) {
      const confirmation$ = this.dialogs.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));
      confirmation$.pipe(
        filter$(yesAnswer),
        switchMap$(() => this.completeFlow.run({
          form: this.form,
          value: this.form.submitValue,
          trigger: 'process',
        }).toFinalState()),
        // We have to wait until next change detection is finished
        // to allow GanttChart to merge state of saved tasks.
        // GanttChart updates physicalTaskIds of all tasks.
        switchMap$(() => this.ngZone.onMicrotaskEmpty.pipe(first$())))
        .subscribe(() => this.openTask(getGanttTaskId(task)));

      confirmation$.pipe(
        filter$(noAnswer))
        .subscribe(() => {
          if (this.isNewSchedule()) {
            this.notifications.error('msg_sch_no_task');
            return;
          }
          this.openTask(getGanttTaskId(task));
        });
    } else {
      this.openTask(getGanttTaskId(task));
    }
  }

  onCloneTask(sourceSibling: GanttTask): void {
    const taskIndex = this.gantt.api.getTaskIndex(getGanttTaskId(sourceSibling));
    this.addClonedTask(sourceSibling, taskIndex + 1);
  }
  taskCanBeDeleted(task: GanttTask): boolean {
    return  this.form.editable
      && (task.canBeDeleted
        || (task.canBeMarkedAsDeleted && !task.markedAsDeleted
          // tslint:disable-next-line:no-magic-numbers
          && (!task.completionPct || task.completionPct < 100)));
  }
  onTaskRemove(task: GanttTask): void {
    if (task.canBeMarkedAsDeleted && !task.markedAsDeleted) {
      const relationsTask = this.gantt.api.getAllTasks()
                                 .filter((item) => item.predecessors?.length &&
                                   item.predecessors?.find((predecessor) => predecessor.taskId === task.taskId),
                                 );
      const taskNames = relationsTask.map((item) => item.name).join(', ') ;
      this.dialogs.confirmation(yesNoConfirmation(relationsTask.length
                                                    ? 'msg_sch_task_pred_mark_as_deleted'
                                                    : 'msg_sch_sum_task_mark_delete',
                                                  {
                                                    taskName: task.name,
                                                    taskNames,
                                                  })).pipe(
        filter$(yesAnswer))
          .subscribe(() => this.gantt.api.markAsDeletedTask(getGanttTaskId(task)));
      return;
    }
    this.dialogs.confirmation(yesNoConfirmation(task.type === ScheduleTaskType.Summary
      ? 'msg_sch_sum_task_delete'
      : 'msg_sch_task_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.gantt.api.removeTask(getGanttTaskId(task)));
  }

  onTaskUndelete(task: GanttTask): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_sch_task_undelete',
                                                {
                                                  taskName: task.name,
                                                })).pipe(
      filter$(yesAnswer))
        .subscribe(() => this.gantt.api.unmarkAsDeletedTask(getGanttTaskId(task)));
  }

  canBeOpened(task: GanttTask): boolean {
    return isGanttTaskCanBeOpened(task);
  }

  canBeCloned(task: GanttTask): boolean {
    // tslint:disable-next-line:no-magic-numbers
    return (task.completionPct || 0) < 100 &&
      (task.type === ScheduleTaskType.CostCenter || task.type === ScheduleTaskType.Empty) &&
      this.scheduleType === ScheduleType.Construction && !task.markedAsDeleted;
  }

  canHaveSubtasks(task: GanttTask): boolean {
    return isGanttTaskCanHaveSubtasks(task);
  }

  onDirtyChange(isDirty: boolean): void {
    this.form.setDirty(isDirty);
  }

  onAutoSave(event: TimerEvent): void {
    const save$ = this.dialogs.confirmation(yesNoConfirmation('msg_unsaved_info')).pipe(
      filter$(yesAnswer),
      switchMap$(() => {
        const flow = this.completeFlow;

        return flow.run({ trigger: 'process', value: this.form.submitValue, form: this.form }).toFinalState();
      }));
    event.waitFor(save$);
  }

  onChangeAutosaveInterval(interval: string): void {
    setLocalValue(SCHEDULE_GANTT_AUTOSAVE_LOCAL_KEY, interval);
  }

  showBaseDatesChange(value: boolean): void {
    this.showBaseDates = value;
  }

  getScheduleRestService(): any {
    switch (this.scheduleType) {
      case ScheduleType.Construction:
        return this.constructionRest;
      case ScheduleType.PreConstruction:
        return this.preConstructionRest;
      case ScheduleType.Maintenance:
        return this.maintenanceRest;
      case ScheduleType.Sales:
        return this.salesRest;
      default:
        return this.constructionRest;
    }
  }

  buildAelNavigationParams(): AelNavigationParams | undefined {
    switch (this.scheduleType) {
      case ScheduleType.Construction:
        return {
          entityType: 'JOB',
          entityId: this.details.jobId,
          // tslint:disable-next-line:no-magic-numbers
          eventTypeIds:  [189, 190, 234],
          label: 'lbl_construction_sch',
        };
      case ScheduleType.PreConstruction:
        return {
          entityType: 'JOB',
          entityId: this.details.jobId,
          // tslint:disable-next-line:no-magic-numbers
          eventTypeIds:  [193, 194, 236],
          label: 'lbl_pre_construction_sch',
        };
      case ScheduleType.Maintenance:
        return {
          entityType: 'JOB',
          entityId: this.details.jobId,
          // tslint:disable-next-line:no-magic-numbers
          eventTypeIds:  [195, 196, 237],
          label: 'lbl_maintenance_sch',
        };
      case ScheduleType.Sales:
        return {
          entityType: 'SALES_FOLDER',
          entityId: this.details.salesFolderId,
          // tslint:disable-next-line:no-magic-numbers
          eventTypeIds:  [191, 192, 235],
          label: 'lbl_sales_sch',
        };
      default:
        return undefined;
    }
  }

  goToAel(): void {
    this.navigations.goToAel(this.buildAelNavigationParams());
  }

  onOpenReport(): void {
    if (this.form.dirty) {
      this.notifications.error('msg_save_first');
      return;
    }
    openInNewWindow(this.reportUrl);
  }

  saveBaseDates(): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_sch_overwrite_base_dates')).pipe(
          filter$(yesAnswer))
        .subscribe(() => {
          this.saveBaseDatesAnswer = Confirmation.Yes;
          this.gantt.api.saveBaseDates();
          if (this.gantt.api.getAllTasks().find((task) => !!task.dirtyType)) {
            this.form.setDirty();
          }
          const restService = this.getScheduleRestService();
          const bounds = this.gantt.api.calculateBounds();
          this.form.value.startDate = bounds.startDate;
          this.form.value.endDate = bounds.endDate;
          this.form.value.duration = bounds.duration;
          this.form.value.tasks = <ScheduleGanttTask[]>this.gantt.api.getAllTasks();
          restService.recalculate(scheduleGanttDetailsToDto(this.form.value)).subscribe(
            (value: ScheduleDetailsDto) => this.reloadData(scheduleGanttDetailsFromDto(value)),
            (error: any) => this.notifications.processAll(error));
        });
  }

  onSizeChange(size: any): void {
    this.business.saveFilter(this.scheduleType, size);
  }

  undo(): void {
    this.gantt.api.undo();
  }

  redo(): void {
    this.gantt.api.redo();
  }

  schSaveBaseDatesDialog(): Observable<any> {
    const tasks = this.form.submitValue.tasks;
    // tslint:disable-next-line:no-magic-numbers
    if (!this.details.isBaseDatesSavedBefore && !!tasks.find((task) => task.completionPct === 100)) {
      return this.dialogs.confirmation(yesNoConfirmation('msg_sch_save_base_dates')).pipe(map$((answer) => {
        this.saveBaseDatesAnswer = answer;
      }));
    }
    return of$(true);
  }

  saveBeforeSaveNote(): any {
    return this.completeFlow.run({
                            form: this.form,
                            value: this.form.submitValue,
                            trigger: 'process',
                          });
  }

  onNextUnconfirmedTask(taskId: string): void {
    this.gantt.api.showTaskInTimeline(taskId);
    this.gantt.api.selectTask(taskId);
  }

  onCheckResourceUsage(task: GanttTask): void {
    this.contactRest
        .checkResourceUsage(task.physicalTaskId)
        .subscribe((res: any) => {
          this.dialogs.openWithInput(scheduleCheckUsageDialogRoute, <any>{
            data: res,
          }).afterClosed();
        });
  }

  private addEmptyTask(parentTaskId?: ID, branchIndex?: number): void {
    const startDate = this.gantt.api.correctTaskStartDate(currentNormalizedDate());
    const nextTaskNumber = this.nextTaskNumber;
    this.nextTaskNumber += 1;
    const name = `${this.translations.get('lbl_new_task')}`;
    this.gantt.api.addTask(
      newScheduleEmptyTask(
        nextTaskNumber,
        name,
        startDate,
        parentTaskId),
      branchIndex);
  }

  private addClonedTask(clonedTask: GanttTask, branchIndex?: number): void {
    const nextTaskNumber = this.nextTaskNumber;
    this.nextTaskNumber += 1;
    const name = `${this.translations.get('lbl_copy_of_task')} ${clonedTask.name}`;
    this.gantt.api.addTask(
      newScheduleCloneTask(
        nextTaskNumber,
        name,
        clonedTask),
      branchIndex);
  }

  private getEditPermission(): Permission {
    return {
      [ScheduleType.Construction]: BuilderPermission.Schedules.Construction.EDIT,
      [ScheduleType.Sales]: BuilderPermission.Schedules.Sales.EDIT,
      [ScheduleType.PreConstruction]: BuilderPermission.Schedules.PreConstruction.EDIT,
      [ScheduleType.Maintenance]: BuilderPermission.Schedules.Maintenance.EDIT,
    }[this.scheduleType];
  }

  private isScheduleEditable(details: ScheduleGanttDetails): boolean {
    switch (this.scheduleType) {
      case ScheduleType.Construction:
        return !details.isFinalized;
      case ScheduleType.PreConstruction:
        return !(details.isFinalized || details.isNotProceeding);
      case ScheduleType.Maintenance:
        return !details.isNotProceeding;
      case ScheduleType.Sales:
        return details.isSalesFolderActive;
      default:
        return false;
    }
  }

  private handleErrors(): void {
    this.notifications.error('msg_he_incorrect_data_in_tables');
    this.gantt.api.navigateToInvalid();
  }

  private openTask(taskId: ID): void {
    const task = this.gantt.api.getTask(taskId);
    if (isDirtyTypeNew(task.dirtyType)) {
      this.notifications.error('msg_sch_no_task');
      return;
    }

    // reset dirty flag to avoid msg_unsaved_info of leave flow
    // (we always call this method after dirty check has been made)
    this.form.resetDirty();

    this.router.navigateToChild([
      '/schedules',
      {
        [ScheduleType.Construction]: 'construction',
        [ScheduleType.Sales]: 'sales',
        [ScheduleType.PreConstruction]: 'pre-construction',
        [ScheduleType.Maintenance]: 'maintenance',
      }[this.scheduleType],
      this.form.value.scheduleId,
      'task',
      task.physicalTaskId]);
  }

  private navigateToScheduleTemplateEdit(scheduleId: ID): void {
    switch (this.scheduleType) {
      case ScheduleType.Construction:
        this.router.switchTo(['schedules', 'construction', scheduleId, 'gantt-chart']);
        break;
      case ScheduleType.Sales:
        this.router.switchTo(['schedules', 'sales', scheduleId, 'gantt-chart']);
        break;
      case ScheduleType.PreConstruction:
        this.router.switchTo(['schedules', 'pre-construction', scheduleId, 'gantt-chart']);
        break;
      case ScheduleType.Maintenance:
        this.router.switchTo(['schedules', 'maintenance', scheduleId, 'gantt-chart']);
        break;
      default:
        break;
    }
  }
}
